import { Campaign, Product, User } from 'app/types/authorization'
import { CompanyData, CompanyProfile } from 'app/types/organization'
import { AuthContext } from 'app/utils/context'
import { clientGetIdToken } from 'app/utils/cognito'
import { captureException } from '@sentry/nextjs'
import { useState, useContext, useEffect, Fragment } from 'react'
import { CheckoutResponse } from 'app/types/checkout'
import { StandardAPIRequest } from 'app/types/api'
import LivedayLoader from './old/animations/LivedayLoader'
import { useTranslation } from 'next-i18next'
import { OrderSummary } from './Billing'
import Currency from 'app/types/currency'
import { CheckoutImage, CompanyLogo } from './old/Image'
import { TFunction } from 'next-i18next'
import { Dialog, Transition } from '@headlessui/react'
import { WalletIcon } from '@heroicons/react/20/solid'
import Link from 'next/link'
import { formatTimeLeft } from 'app/utils/time'
import {
  SurfboardProvider,
  PaymentElement,
  useSurfboard,
  SurfboardPaymentStatus,
  SurfboardPaymentMethod,
} from './Surfboard'

// Component for input field
const InputField: React.FC<{
  id: string
  label: string
  value: string
  onChange: React.ChangeEventHandler<HTMLInputElement>
  onBlur?: () => void
  error?: string
  type?: string
  optional?: boolean
  t: TFunction
}> = ({
  id,
  label,
  value,
  onChange,
  onBlur,
  error,
  type = 'text',
  optional = false,
  t,
}) => (
  <div className="sm:col-span-2">
    <label htmlFor={id} className="block text-sm font-medium text-gray-700">
      {label} {optional && `(${t('optional')})`}
    </label>
    <div className="mt-1">
      <input
        type={type}
        id={id}
        name={id}
        autoComplete={id}
        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500 sm:text-sm"
        value={value}
        onChange={onChange}
        onBlur={onBlur}
      />
      {error && <p className="mt-1 text-red-500">{error}</p>}
    </div>
  </div>
)

const PaymentModal = ({
  open,
  closeModal,
  setShowFailureMessage,
  checkoutSession,
  email,
  phoneNumber,
  billingName,
  billingAddress,
  billingCity,
  billingPostalCode,
  setFinished,
}: {
  open: boolean
  closeModal: () => void
  setShowFailureMessage: (message: string) => void
  checkoutSession: CheckoutResponse
  email: string
  phoneNumber: string
  billingName: string
  billingAddress: string
  billingCity: string
  billingPostalCode: string
  setFinished: (finished: boolean) => void
}) => {
  const { t } = useTranslation('common')
  const {
    supportedPaymentMethods,
    initiatePayments,
    addCustomerInformationOnce,
    setErrorCallback,
    setPaymentStatusCallback,
    isReady,
  } = useSurfboard()
  const [supportedMethods, setSupportedMethods] = useState<
    SurfboardPaymentMethod[]
  >([])
  const [swishQRData, setSwishQRData] = useState<string | null>(null)
  const [paymentStatus, setPaymentStatus] = useState<
    SurfboardPaymentStatus | 'INITIAL'
  >('INITIAL')

  useEffect(() => {
    if (!isReady) return

    // Get supported payment methods
    setSupportedMethods(supportedPaymentMethods)

    // Set up callbacks once
    setErrorCallback((code: string, message: string) => {
      const errorMessage = `Error [${code}]: ${message}`
      console.error(errorMessage)

      switch (code) {
        case 'ON_008':
          setShowFailureMessage('Please provide complete customer information')
          break
        case 'ON_009':
          setShowFailureMessage('Phone number is required for Swish payment')
          break
        case 'ON_016':
          setShowFailureMessage(
            'Invalid card details. Please check and try again'
          )
          break
        case 'ON_017':
          setShowFailureMessage('Email is required for this payment method')
          break
        case 'ON_018':
          setShowFailureMessage(
            'Billing address is required for this payment method'
          )
          break
        default:
          setShowFailureMessage(message)
      }
    })

    setPaymentStatusCallback(
      (data: {
        paymentStatus: SurfboardPaymentStatus
        failureReason?: string
      }) => {
        console.log(`Payment status update: ${JSON.stringify(data)}`)
        setPaymentStatus(data.paymentStatus)
        switch (data.paymentStatus) {
          case 'PAYMENT_COMPLETED':
            setFinished(true)
            closeModal()
            break
          case 'PAYMENT_FAILED':
            setShowFailureMessage(data.failureReason || 'Payment failed')
            break
          case 'PAYMENT_CANCELLED':
            setShowFailureMessage('Payment was cancelled')
            break
          case 'PENDING':
            console.log('Payment is pending')
            break
          case 'PAYMENT_INITIATED':
            console.log('Payment initiated')
            break
          case 'PAYMENT_PROCESSING':
            console.log('Payment is processing')
            break
          case 'PAYMENT_PROCESSED':
            console.log('Payment has been processed')
            break
        }
      }
    )

    // Add customer information once
    const phoneCountryCode = phoneNumber.startsWith('+46') ? '+46' : '+358'
    const countryCode = phoneNumber.startsWith('+46') ? 'SE' : 'FI'
    const cleanPhoneNumber = phoneNumber.replace(/^\+46|\+358/, '')

    addCustomerInformationOnce({
      phone: { countryCode: phoneCountryCode, number: cleanPhoneNumber },
      name: billingName,
      email: email,
      billingAddress: {
        city: billingCity,
        postalCode: billingPostalCode,
        countryCode: countryCode,
        addressLine1: billingAddress,
      },
    })
  }, [
    supportedPaymentMethods,
    initiatePayments,
    addCustomerInformationOnce,
    isReady,
    setErrorCallback,
    setPaymentStatusCallback,
    phoneNumber,
    billingName,
    email,
    billingCity,
    billingPostalCode,
    billingAddress,
    closeModal,
    setFinished,
    setShowFailureMessage,
  ])

  const initiatePayment = async (method: string) => {
    try {
      if (!initiatePayments) {
        throw new Error('Payment initiation not supported')
      }
      const paymentAttempt = await initiatePayments(method)

      if (
        method === 'NSWISH' &&
        !/Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
      ) {
        const qrData = paymentAttempt.getSwishQRData
        if (qrData) {
          setSwishQRData(qrData)
        }
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to initiate payment'
      setShowFailureMessage(errorMessage)
    }
  }

  const renderPaymentStatus = () => {
    switch (paymentStatus) {
      case 'PAYMENT_PROCESSING':
        return (
          <div className="flex items-center gap-x-6 bg-blue-600 px-6 py-2.5 sm:px-3.5 sm:before:flex-1">
            <p className="text-sm/6 text-white">
              <strong className="font-semibold">
                {t('processing-payment')}
              </strong>
            </p>
          </div>
        )
      case 'PAYMENT_INITIATED':
        return (
          <div className="flex items-center gap-x-6 bg-green-600 px-6 py-2.5 sm:px-3.5 sm:before:flex-1">
            <p className="text-sm/6 text-white">
              <strong className="font-semibold">
                {t('payment-initiated')}
              </strong>
            </p>
          </div>
        )
      case 'PAYMENT_CANCELLED':
        return (
          <div className="flex items-center gap-x-6 bg-red-600 px-6 py-2.5 sm:px-3.5 sm:before:flex-1">
            <p className="text-sm/6 text-white">
              <strong className="font-semibold">
                {t('payment-cancelled')}
              </strong>
            </p>
          </div>
        )
      case 'PAYMENT_COMPLETED':
        return (
          <div className="flex items-center gap-x-6 bg-green-600 px-6 py-2.5 sm:px-3.5 sm:before:flex-1">
            <p className="text-sm/6 text-white">
              <strong className="font-semibold">
                {t('payment-completed')}
              </strong>
            </p>
          </div>
        )
      case 'PAYMENT_FAILED':
        return (
          <div className="flex items-center gap-x-6 bg-red-600 px-6 py-2.5 sm:px-3.5 sm:before:flex-1">
            <p className="text-sm/6 text-white">
              <strong className="font-semibold">{t('payment-failed')}</strong>
            </p>
          </div>
        )
      case 'PAYMENT_PROCESSED':
        return (
          <div className="flex items-center gap-x-6 bg-green-600 px-6 py-2.5 sm:px-3.5 sm:before:flex-1">
            <p className="text-sm/6 text-white">
              <strong className="font-semibold">
                {t('payment-processed')}
              </strong>
            </p>
          </div>
        )
      default:
        return null
    }
  }

  const renderPaymentContent = () => {
    return (
      <>
        {paymentStatus === 'PAYMENT_PROCESSING' && (
          <div className="text-center">
            <LivedayLoader />
          </div>
        )}

        {paymentStatus === 'PAYMENT_INITIATED' && swishQRData && (
          <div className="mt-4 rounded-lg bg-white p-6">
            <h3 className="mb-4 text-lg font-medium">
              {t('scan-with-swish-app')}
            </h3>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={swishQRData}
              alt="Swish QR Code"
              className="mx-auto h-64 w-64"
            />
          </div>
        )}

        {paymentStatus === 'INITIAL' && (
          <>
            <div className="my-2 rounded-lg bg-white p-3">
              <PaymentElement type="CARD" />
              <button
                onClick={() => initiatePayment('CARD')}
                className="mt-2 flex w-full items-center justify-center space-x-2 rounded-lg bg-gray-50 p-4 hover:bg-white"
              >
                <span>{t('pay-with-card')}</span>
              </button>
            </div>

            <div className="my-2">
              <PaymentElement type="APPLE_PAY" />
            </div>
            <div className="my-2">
              <PaymentElement type="GOOGLE_PAY" />
            </div>

            {supportedMethods.includes('KLARNA') && (
              <div className="my-2">
                <PaymentElement type="KLARNA" />
              </div>
            )}

            {supportedMethods.includes('NSWISH') && (
              <>
                {swishQRData && (
                  <div className="my-2 rounded-lg bg-white p-6">
                    <h3 className="mb-4 text-lg font-medium">
                      {t('scan-with-swish-app')}
                    </h3>
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={swishQRData}
                      alt="Swish QR Code"
                      className="mx-auto h-64 w-64"
                    />
                  </div>
                )}
                <button
                  onClick={() => initiatePayment('NSWISH')}
                  className="my-2 w-full rounded-md border border-gray-300 bg-white px-4 py-2 text-lg font-medium text-gray-900 shadow-sm hover:bg-gray-900 hover:text-white focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  {t('pay-with-swish')}
                </button>
              </>
            )}
          </>
        )}
      </>
    )
  }

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={closeModal}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-gray-900 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                {renderPaymentStatus()}
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <WalletIcon
                      className="h-6 w-6 text-green-600"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="mt-3 w-full">
                    <Dialog.Title
                      as="h3"
                      className="text-center text-2xl font-semibold leading-6 text-gray-100"
                    >
                      {(
                        (checkoutSession.checkout_session.cart.total_price ??
                          0) / 100
                      ).toFixed(2)}{' '}
                      {checkoutSession.checkout_session.cart.currency?.toString()}
                    </Dialog.Title>
                    <div className="mt-4">
                      {renderPaymentContent()}

                      {paymentStatus === 'INITIAL' && (
                        <button
                          type="button"
                          className="relative my-2 w-full cursor-pointer rounded-md border border-gray-300 bg-gray-800 px-4 py-2 text-center text-sm font-medium text-white shadow-sm"
                          onClick={closeModal}
                        >
                          {t('cancel')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <Link
                    href="/privacy-policy"
                    className="text-sm text-gray-500"
                  >
                    {t('privacy-policy')}
                  </Link>
                  <span className="mx-4 text-sm text-gray-500">|</span>
                  <Link
                    href="/terms_of_service"
                    className="text-sm text-gray-500"
                  >
                    {t('terms-of-conditions')}
                  </Link>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}

// Props for the CheckoutCart component
interface SurfboardCartProps {
  user: User | undefined
  eventSk: string
  organization: string
  items: Product[]
  campaigns: Campaign[]
  setShowFailureMessage: React.Dispatch<React.SetStateAction<string>>
  companyProfile: CompanyProfile | null
  companyData: CompanyData[]
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>
  refetchCart: () => Promise<void>
  name?: string
  email?: string
  phoneNumber?: string
  poolId?: string
  userPoolId?: string
}

export function SurfboardCheckoutCart({
  userPoolId,
  poolId,
  eventSk,
  ...props
}: SurfboardCartProps) {
  const [loading, setLoading] = useState<boolean>(false)
  const [checkoutSession, setCheckoutSession] = useState<
    CheckoutResponse | undefined
  >(undefined)
  const { state } = useContext(AuthContext)
  const { i18n } = useTranslation()

  useEffect(() => {
    const fetchCheckoutSession = async () => {
      setLoading(true)
      try {
        const idToken = await clientGetIdToken()
        let headers: HeadersInit = {
          'Content-Type': 'application/json',
          'Cart-Token': state.cartToken,
        }
        if (idToken) {
          headers.Authorization = `Bearer ${idToken}`
        }
        if (poolId && userPoolId === undefined) {
          headers['Company-Pool-Token'] = poolId
        }
        if (userPoolId && poolId) {
          headers['Public-Pool-Token'] = poolId
          headers['Public-Pool-User-Token'] = userPoolId
        }

        const response = await fetch(
          `${process.env.NEXT_PUBLIC_API_URL}/checkout/${eventSk}/billing_session_surfboard`,
          {
            method: 'PUT',
            headers,
          }
        )

        const checkoutSessionData: StandardAPIRequest<CheckoutResponse> =
          await response.json()
        if (checkoutSessionData.error) {
          throw new Error(checkoutSessionData.error.message)
        }
        setCheckoutSession(checkoutSessionData.data ?? undefined)
      } catch (err) {
        captureException(err)
      } finally {
        setLoading(false)
      }
    }

    fetchCheckoutSession()
  }, [eventSk, state.cartToken, poolId, userPoolId])

  if (
    checkoutSession === undefined ||
    loading ||
    !checkoutSession.surfboard?.public_key ||
    !checkoutSession.surfboard?.order_id ||
    !checkoutSession.surfboard?.nonce
  ) {
    return <LivedayLoader />
  }

  return (
    <SurfboardProvider
      publicKey={checkoutSession.surfboard.public_key}
      orderId={checkoutSession.surfboard.order_id}
      nonce={checkoutSession.surfboard.nonce}
      options={{
        locale: i18n.language,
      }}
    >
      <InnerSurfboardCheckoutCart
        checkoutSession={checkoutSession}
        eventSk={eventSk}
        poolId={poolId}
        userPoolId={userPoolId}
        {...props}
      />
    </SurfboardProvider>
  )
}

function InnerSurfboardCheckoutCart({
  user,
  eventSk,
  setShowFailureMessage,
  companyProfile,
  name: existingName,
  email: existingEmail,
  phoneNumber: existingPhoneNumber,
  poolId,
  checkoutSession,
}: SurfboardCartProps & {
  checkoutSession: CheckoutResponse
}) {
  const { state } = useContext(AuthContext)
  const [finished, setFinished] = useState(false)
  const [open, setOpen] = useState(false)
  const { isReady } = useSurfboard()

  const [email, setEmail] = useState<string>(existingEmail ?? user?.email ?? '')
  const [phoneNumber, setPhoneNumber] = useState<string>(
    existingPhoneNumber ?? user?.phone_number ?? ''
  )
  const [billingName, setBillingName] = useState<string>(
    existingName ?? user?.billing_address?.name ?? ''
  )
  const [billingAddress, setBillingAddress] = useState<string>(
    user?.billing_address?.address ?? ''
  )
  const [billingCity, setBillingCity] = useState<string>(
    user?.billing_address?.city ?? ''
  )
  const [billingPostalCode, setBillingPostalCode] = useState<string>(
    user?.billing_address?.postal_code ?? ''
  )

  const { t } = useTranslation('common')
  const [cartTimeToDelete, setCartTimeToDelete] = useState<number | null>(null)
  const [timeLeft, setTimeLeft] = useState<number | null>(null)

  useEffect(() => {
    if (
      checkoutSession?.checkout_session.cart.time_to_delete !== null &&
      checkoutSession?.checkout_session.cart.time_to_delete !== undefined
    ) {
      const currentTime = Math.floor(new Date().getTime() / 1000)
      const diff =
        checkoutSession.checkout_session.cart.time_to_delete - currentTime
      setCartTimeToDelete(diff > 0 ? diff : null)
      setTimeLeft(diff > 0 ? diff : null)
    }
  }, [checkoutSession?.checkout_session.cart])

  useEffect(() => {
    const intervalId = setInterval(() => {
      setTimeLeft((timeLeft) =>
        timeLeft !== null
          ? timeLeft > 0
            ? timeLeft - 1
            : timeLeft
          : cartTimeToDelete
      )
    }, 1000)

    return () => clearInterval(intervalId)
  }, [cartTimeToDelete])

  useEffect(() => {
    if (timeLeft === 0) {
      alert(t('time-has-run-out-the-ticket-has-been-released'))
      window.location.reload()
    }
  }, [timeLeft, t])

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!isReady) return
    const idToken = await clientGetIdToken()
    try {
      let headers: HeadersInit = {
        'Content-Type': 'application/json',
        'Cart-Token': state.cartToken,
      }
      if (idToken) {
        headers.Authorization = `Bearer ${idToken}`
      }
      if (poolId) {
        headers['Company-Pool-Token'] = poolId
      }
      let response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/checkout/${eventSk}/billing`,
        {
          method: 'PUT',
          body: JSON.stringify({
            email,
            phone_number: phoneNumber,
            shipping_name: billingName,
            shipping_address: billingAddress,
            shipping_city: billingCity,
            shipping_postal_code: billingPostalCode,
            billing_name: billingName,
            billing_address: billingAddress,
            billing_city: billingCity,
            billing_postal_code: billingPostalCode,
            checkout_session_uuid:
              checkoutSession.checkout_session.uuid ?? undefined,
          }),
          headers,
        }
      )
      await response.json()
      setOpen(true)
    } catch (err) {
      captureException(err)
      setShowFailureMessage('')
    }
  }

  return (
    <div className="flex flex-col-reverse bg-white sm:flex-row sm:flex-nowrap">
      {open && (
        <PaymentModal
          open={open}
          closeModal={() => setOpen(false)}
          setShowFailureMessage={setShowFailureMessage}
          checkoutSession={checkoutSession}
          email={email}
          phoneNumber={phoneNumber}
          billingName={billingName}
          billingAddress={billingAddress}
          billingCity={billingCity}
          billingPostalCode={billingPostalCode}
          setFinished={setFinished}
        />
      )}

      <div className="flex h-full w-full flex-row justify-center bg-white px-4 pb-20 sm:w-2/5 sm:px-20">
        {finished ? (
          <div className="my-14">
            <div className="h-20">
              <CompanyLogo imageLink={companyProfile?.logo ?? undefined} />
            </div>
            <div className="mt-8">
              <h2 className="text-3xl font-bold">
                {t('thank-you-for-supporting', {
                  companyName: companyProfile?.name,
                })}
              </h2>
              <p className="mt-4">{t('payment-successful-message')}</p>
              <p className="mt-2 text-xl font-bold">
                {t('cheer-for-company', {
                  companyName: companyProfile?.name,
                })}
              </p>
              <button
                onClick={() => window.location.reload()}
                style={{
                  backgroundColor: companyProfile?.secondary_main ?? undefined,
                }}
                className="mt-5 rounded-md border border-transparent bg-gray-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 focus:ring-offset-gray-50"
              >
                {t('buy-again')}
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="w-full">
            {timeLeft !== null &&
              timeLeft > 0 &&
              checkoutSession.checkout_session.cart.items.filter(
                (item) => item.Ticket !== undefined
              ).length > 0 && (
                <div>
                  <p className="mb-4 text-xs font-bold text-gray-900">
                    {`${t('time-left')}: ${formatTimeLeft(timeLeft, t)}`}
                  </p>
                </div>
              )}

            <div>
              <h3 className="mt-10 text-lg font-medium text-gray-900">
                {t('contact-information')}
              </h3>
              <div className="mt-6 grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                <InputField
                  id="name"
                  label={t('name')}
                  value={billingName}
                  onChange={(e) => setBillingName(e.target.value)}
                  t={t}
                />
                <InputField
                  id="email"
                  label={t('email')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  t={t}
                />
                <InputField
                  id="phone_number"
                  label={t('phone-number')}
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  type="tel"
                  t={t}
                />
              </div>
            </div>

            <div className="mt-10">
              <h3 className="text-lg font-medium text-gray-900">
                {t('billing-information')}
              </h3>
              <div className="mt-6 grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                <InputField
                  id="billing-address"
                  label={t('address')}
                  value={billingAddress}
                  onChange={(e) => setBillingAddress(e.target.value)}
                  optional
                  t={t}
                />
                <InputField
                  id="billing-postal-code"
                  label={t('postal-code')}
                  value={billingPostalCode}
                  onChange={(e) => setBillingPostalCode(e.target.value)}
                  optional
                  t={t}
                />
                <InputField
                  id="billing-city"
                  label={t('city')}
                  value={billingCity}
                  onChange={(e) => setBillingCity(e.target.value)}
                  optional
                  t={t}
                />
              </div>
            </div>

            <div className="mt-6 space-y-4">
              <button
                type="submit"
                className="w-full rounded-md border border-gray-300 bg-white px-4 py-2 text-lg font-medium text-gray-900 shadow-sm hover:bg-gray-900 hover:text-white focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t('pay-now')}
              </button>
            </div>
          </form>
        )}
      </div>

      <div className="flex w-full flex-row items-center justify-center bg-white sm:w-3/5">
        <div className="relative flex h-full w-full justify-center">
          <div className="absolute inset-0 h-full w-full">
            <CheckoutImage
              relative
              imageLink={companyProfile?.checkout_image ?? undefined}
              alt={''}
            />
          </div>

          <div className="z-10 w-5/6 max-w-2xl">
            <div className="mb-3 mt-12 flex flex-row justify-start">
              <div>
                <p
                  style={{
                    color: companyProfile?.primary_main ?? undefined,
                  }}
                  className="font-bold"
                >
                  {t('your-order')}
                </p>
              </div>
            </div>

            <OrderSummary
              items={checkoutSession.checkout_session.cart.items}
              currency={
                checkoutSession.checkout_session.cart.currency ?? Currency.SEK
              }
              checkoutSession={checkoutSession}
              t={t}
              totalVAT={0}
              ticketVAT={0}
              itemVAT={0}
              kioskVAT={0}
              restaurantVAT={0}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
